/**
 * A PLACE FOR THE IN-APP BROWSER'S BACK GESTURE TO GO.
 *
 * The owner, opening a product page from a Facebook ad on his phone, swiped through the photographs
 * and the page slid out from under his finger, back into Facebook. Instagram's and Facebook's
 * in-app browsers — a WKWebView on iOS, a Chrome WebView on Android — read a horizontal swipe that
 * starts at the screen's edge as "back", and with nothing in the history to go back to they close
 * the browser instead. The carousel takes the swipe once the finger has moved, but the edge was the
 * system's before that; `touch-action` cannot refuse a gesture the OS recognises first.
 *
 * So the gesture is given somewhere harmless to go. In an in-app browser the entry the shopper
 * arrived on is marked as the ROOT, and a second entry at the same address — the GUARD — is pushed
 * on top of it before React Router boots. A swipe back lands on the root, which is the same page,
 * and `popstate` pushes the guard again, so the next swipe has the same place to go. Everything
 * above the guard is ordinary history: React Router pushes its entries over it, and a real back
 * (checkout → product) pops one of those and is left alone. Only a pop that lands on the root
 * marker is answered; the router never reads either marker (it keeps `usr` and `key` of its own).
 *
 * THE COST, AND IT IS DELIBERATE: on the page the shopper arrived on, the in-app toolbar's own back
 * arrow now goes nowhere — before, it closed the browser, which is what the swipe was doing. The X
 * still closes it. Nothing is installed anywhere else: Safari and Chrome keep their own gestures,
 * and their back leaves for the site the shopper came from, as it should.
 */
import { isInAppBrowser } from './stableViewport';

/** On the entry the shopper arrived on: a pop that lands here is the swipe going past our start. */
export const IN_APP_ROOT_STATE = 'stylonInAppRoot';
/** On the entry pushed over it, so the swipe has somewhere to go. */
export const IN_APP_GUARD_STATE = 'stylonInAppGuard';

/**
 * Mark the current entry as the root and push the guard over it, in an in-app browser only.
 * Returns an uninstall function; a no-op anywhere else.
 */
export function installInAppBackGuard(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win || !win.history || typeof win.history.pushState !== 'function') return () => {};
  if (!isInAppBrowser(win.navigator && win.navigator.userAgent)) return () => {};
  const { history, location } = win;
  const guard = () => {
    try {
      history.pushState({ [IN_APP_GUARD_STATE]: true }, '', location.href);
    } catch (_) {
      // iOS caps pushState at a hundred calls in thirty seconds; past it the page simply stays as it is.
    }
  };
  try {
    // Merged, not replaced: whatever the browser or the server put on the entry stays on it.
    const state = history.state && typeof history.state === 'object' ? history.state : {};
    history.replaceState({ ...state, [IN_APP_ROOT_STATE]: true }, '', location.href);
  } catch (_) {
    return () => {};
  }
  /* CHROME SKIPS AN ENTRY THE SHOPPER NEVER ASKED FOR (measured on the Pixel 9 emulator, 2026-10-02). Chrome's
     history-manipulation rule marks an entry pushed without a user gesture as skippable by back, so a guard pushed
     at load was stepped over and the first edge swipe left the site. An entry pushed after the shopper has touched
     the page counts, and so does one re-pushed on the touch after a swipe landed on the root — four swipes in a row
     stayed on the page that way. iOS's WKWebView honours the entry as pushed at load (verified the same day in the
     Simulator), and gets nothing to wait for. */
  const chromeEngine = /\bAndroid\b/i.test((win.navigator && win.navigator.userAgent) || '');
  const GESTURES = ['touchstart', 'pointerdown', 'keydown'];
  let armed = null;
  const onNextGesture = () => {
    if (armed) return;
    armed = () => {
      GESTURES.forEach((t) => win.removeEventListener(t, armed, true));
      armed = null;
      guard();
    };
    GESTURES.forEach((t) => win.addEventListener(t, armed, true));
  };
  if (chromeEngine) onNextGesture();
  else guard();
  const onPop = (event) => {
    const landed = event && event.state;
    if (!landed || !landed[IN_APP_ROOT_STATE]) return;
    if (chromeEngine) onNextGesture();
    else guard();
  };
  win.addEventListener('popstate', onPop);
  return () => {
    win.removeEventListener('popstate', onPop);
    if (armed) GESTURES.forEach((t) => win.removeEventListener(t, armed, true));
  };
}
